import { baggageUtils, getEnv } from '@opentelemetry/core';
import { OTLPExporterError } from '@opentelemetry/otlp-exporter-base';
import { OTLPTraceExporter } from '@opentelemetry/exporter-trace-otlp-http';

const MIME_TYPE_JSON = 'application/json';

function filterDynamicHeaders(headers) {
  const dynamicHeaders = {};
  Object.entries(headers).forEach(([key, value]) => {
    if (typeof value === 'function') {
      dynamicHeaders[key] = value;
    }
    // everything else handled by filterStaticHeaders
  });
  return dynamicHeaders;
}

function filterStaticHeaders(headers) {
  const staticHeaders = {};
  Object.entries(headers).forEach(([key, value]) => {
    if (!value) {
      console.warn(`header "${key}" is missing value and will be ignored`);
      return;
    }
    if (typeof value === 'function') {
      // handled by filterDynamicHeaders
      return;
    }
    staticHeaders[key] = String(value);
  });
  return staticHeaders;
}

/**
 * Exporter specifically for the JSON-over-HTTP variation of the OpenTelemetry Line Protocol (OTLP).
 * This is an extension and replacement of the OTLPTraceExporter from the
 * @opentelemetry/exporter-trace-otlp-http package to allow customization of behavior for TRP
 * purposes.
 */
export default class Exporter extends OTLPTraceExporter {
  urlProvider;
  useBeacon;
  dynamicHeaders;
  staticHeaders;

  constructor(config = {}) {
    super(config);
    this.urlProvider = config.urlProvider;
    this.useBeacon =
      typeof navigator.sendBeacon === 'function' && !config.headers;
    if (config.headers) {
      this.dynamicHeaders = filterDynamicHeaders(config.headers);
      this.staticHeaders = Object.assign(
        {
          Accept: MIME_TYPE_JSON,
          'Content-Type': MIME_TYPE_JSON
        },
        filterStaticHeaders(config.headers),
        baggageUtils.parseKeyPairsIntoRecord(
          getEnv().OTEL_EXPORTER_OTLP_HEADERS
        ),
        baggageUtils.parseKeyPairsIntoRecord(
          getEnv().OTEL_EXPORTER_OTLP_TRACES_HEADERS
        )
      );
    } else {
      this.dynamicHeaders = {};
      this.staticHeaders = {};
    }
  }

  buildHeaders() {
    const headers = {
      ...this.staticHeaders
    };
    Object.entries(this.dynamicHeaders).forEach(([key, getter]) => {
      const value = getter();
      if (value) {
        headers[key] = String(value);
      }
    });
    return headers;
  }

  coordinateSend(sendPromise) {
    this._sendingPromises.push(sendPromise);
    const popPromise = () => {
      const index = this._sendingPromises.indexOf(sendPromise);
      this._sendingPromises.splice(index, 1);
    };
    sendPromise.then(popPromise, popPromise);
  }

  resolveUrl() {
    if (this.urlProvider) {
      return this.urlProvider();
    }
    return this.url;
  }

  /**
   * Send spans using browser navigator.sendBeacon(). This is the preferred method of sending
   * analytics but is potentially unavailable as a browser feature. Additionally, its usage is
   * limited and does not support all HTTP request functionality. For example, it does not support
   * custom headers.
   *
   * see: https://developer.mozilla.org/en-US/docs/Web/API/Beacon_API
   *
   * @param body
   */
  sendWithBeacon(body) {
    if (!this.useBeacon) {
      return undefined;
    }
    const blobPropertyBag = {
      type: MIME_TYPE_JSON
    };
    return new Promise((resolve, reject) => {
      if (
        navigator.sendBeacon(
          this.resolveUrl(),
          new Blob([body], blobPropertyBag)
        )
      ) {
        console.debug('sendBeacon - can send', body);
        resolve();
      } else {
        reject(new OTLPExporterError(`sendBeacon - cannot send ${body}`));
      }
    });
  }

  /**
   * function to send spans using browser fetch() when navigator.sendBeacon() is not available or
   * cannot serve the request (ex: custom headers).
   *
   * see: https://developer.mozilla.org/en-US/docs/Web/API/Fetch_API
   *
   * @param body
   */
  async sendWithFetch(body) {
    try {
      const response = await fetch(this.resolveUrl(), {
        body: JSON.stringify(body),
        credentials: 'include',
        headers: this.buildHeaders(),
        method: 'POST'
      });

      if (!response.ok) {
        console.log(
          `failed to export with fetch (status: ${response.status})`,
          response.status
        );
      }
    } catch (e) {
      console.log(e);
    }
  }

  /**
   * Implements the logic necessary to POST the passed spans as JSON over HTTP to an OpenTelemetry
   * collector.
   *
   * @param items
   * @param onSuccess
   * @param onError
   */
  send(items, onSuccess, onError) {
    if (this._shutdownOnce.isCalled) {
      console.debug('Shutdown already started. Cannot send objects');
      return;
    }
    const serviceRequest = this.convert(items);
    const body = JSON.stringify(serviceRequest);
    const promise = this.sendWithFetch(body);
    this.coordinateSend(promise.then(onSuccess, onError));
  }
}
